import React, { useState, useEffect } from "react";
import Layout from "../../layout/Main/Layout";
import {
  Typography,
  Button,
  Paper,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Avatar,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  CircularProgress,
  Chip,
  TextareaAutosize,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CategoryIcon from "@mui/icons-material/Category";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { DataGrid } from "@mui/x-data-grid";
import { get, post, put, del, postFiles } from "../../config/axios";
import toast from "react-hot-toast";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import Quill styles

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [currentBlog, setCurrentBlog] = useState({
    title: "",
    slug: "",
    coverImage: "",
    coverImagePreview: "",
    category: "",
    intro: "",
    contentSections: [{ heading: "", content: "", image: "", imagePreview: "" }],
    metaTitle: "",
    metaDescription: "",
    metaKeywords: [],
    publishDate: null,
    status: "Draft",
  });
  const [editIndex, setEditIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState({ name: "", _id: null });

  // Quill toolbar configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link',
  ];

  const fetchBlogCategories = async () => {
    try {
      const response = await get("/api/dashboard/website/blogcategories");
      setCategories(response);
    } catch (error) {
      console.error("Error fetching blog categories:", error);
      toast.error("Failed to load categories");
    }
  };

  useEffect(() => {
    fetchBlogCategories();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await get("/api/dashboard/website/blogs");
      setBlogs(response);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toast.error("Failed to load blogs");
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleImageUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await postFiles("/api/app/user/uploadImage", formData);
      return response.data.url;
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
      return null;
    }
  };

  const handleAddOrEdit = async () => {
    if (!currentBlog.title.trim() || !currentBlog.intro.trim() || !currentBlog.category) {
      toast.error("Title, intro, and category are required!");
      return;
    }

    setIsSubmitting(true);
    try {
      const blogData = {
        title: currentBlog.title,
        slug: currentBlog.slug || currentBlog.title.toLowerCase().replace(/\s+/g, "-"),
        intro: currentBlog.intro,
        contentSections: currentBlog.contentSections,
        category: currentBlog.category,
        metaTitle: currentBlog.metaTitle,
        metaDescription: currentBlog.metaDescription,
        metaKeywords: currentBlog.metaKeywords,
        publishDate: currentBlog.publishDate,
        status: currentBlog.status,
      };

      if (currentBlog.coverImage instanceof File) {
        const coverImageUrl = await handleImageUpload(currentBlog.coverImage);
        if (coverImageUrl) blogData.coverImage = coverImageUrl;
      } else if (currentBlog.coverImage) {
        blogData.coverImage = currentBlog.coverImage;
      }

      for (let section of blogData.contentSections) {
        if (section.image instanceof File) {
          const sectionImageUrl = await handleImageUpload(section.image);
          if (sectionImageUrl) section.image = sectionImageUrl;
        }
      }

      if (editIndex !== null) {
        await put(`/api/dashboard/website/blogs/${currentBlog._id}`, blogData);
        toast.success("Blog updated successfully");
      } else {
        await post("/api/dashboard/website/blogs", blogData);
        toast.success("Blog added successfully");
      }

      fetchBlogs();
      handleClose();
    } catch (error) {
      console.error("Error saving blog:", error);
      toast.error("Failed to save blog");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (index) => {
    try {
      const blogToDelete = blogs[index];
      await del(`/api/dashboard/website/blogs/${blogToDelete._id}`);
      toast.success("Blog deleted successfully");
      fetchBlogs();
    } catch (error) {
      console.error("Error deleting blog:", error);
      toast.error("Failed to delete blog");
    }
  };

  const handleEdit = (index) => {
    const blogToEdit = blogs[index];
    setCurrentBlog({
      ...blogToEdit,
      _id: blogToEdit._id,
      coverImagePreview: blogToEdit.coverImage,
      category: blogToEdit.category?._id || blogToEdit.category || "",
      contentSections: blogToEdit.contentSections || [{ heading: "", content: "", image: null, imagePreview: null }],
      publishDate: blogToEdit.publishDate ? dayjs(blogToEdit.publishDate) : null,
    });
    setEditIndex(index);
    setOpen(true);
  };

  const handleClose = () => {
    if (currentBlog.coverImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(currentBlog.coverImagePreview);
    }
    currentBlog.contentSections.forEach((section) => {
      if (section.imagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(section.imagePreview);
      }
    });

    setOpen(false);
    setCurrentBlog({
      title: "",
      slug: "",
      coverImage: null,
      coverImagePreview: null,
      category: "",
      intro: "",
      contentSections: [{ heading: "", content: "", image: null, imagePreview: null }],
      metaTitle: "",
      metaDescription: "",
      metaKeywords: [],
      publishDate: null,
      status: "Draft",
    });
    setEditIndex(null);
  };

  const handleFileChange = (e, type, sectionIndex = null) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      if (sectionIndex !== null) {
        setCurrentBlog((prev) => {
          const updatedSections = [...prev.contentSections];
          updatedSections[sectionIndex] = {
            ...updatedSections[sectionIndex],
            image: file,
            imagePreview: url,
          };
          return { ...prev, contentSections: updatedSections };
        });
      } else {
        setCurrentBlog((prev) => ({
          ...prev,
          [type]: file,
          [`${type}Preview`]: url,
        }));
      }
    }
  };

  const handleCategoryChange = (e) => {
    setCurrentBlog((prev) => ({ ...prev, category: e.target.value }));
  };

  const handleAddSection = () => {
    setCurrentBlog((prev) => ({
      ...prev,
      contentSections: [...prev.contentSections, { heading: "", content: "", image: null, imagePreview: null }],
    }));
  };

  const handleSectionChange = (index, field, value) => {
    setCurrentBlog((prev) => {
      const updatedSections = [...prev.contentSections];
      updatedSections[index] = { ...updatedSections[index], [field]: value };
      return { ...prev, contentSections: updatedSections };
    });
  };

  const handleMetaKeywordsChange = (e) => {
    const keywords = e.target.value.split(",").map((keyword) => keyword.trim()).filter(Boolean);
    setCurrentBlog((prev) => ({ ...prev, metaKeywords: keywords }));
  };

  const handleCategoryDialogOpen = () => {
    setCategoryDialogOpen(true);
  };

  const handleCategoryDialogClose = () => {
    setCategoryDialogOpen(false);
    setCurrentCategory({ name: "", _id: null });
  };

  const handleCategorySubmit = async () => {
    if (!currentCategory.name.trim()) {
      toast.error("Category name is required!");
      return;
    }

    try {
      if (currentCategory._id) {
        await put(`/api/dashboard/website/blogcategories/${currentCategory._id}`, {
          name: currentCategory.name,
        });
        toast.success("Category updated successfully");
      } else {
        await post("/api/dashboard/website/blogcategories", {
          name: currentCategory.name,
        });
        toast.success("Category added successfully");
      }
      fetchBlogCategories();
      handleCategoryDialogClose();
    } catch (error) {
      console.error("Error saving category:", error);
      toast.error("Failed to save category");
    }
  };

  const handleEditCategory = (category) => {
    setCurrentCategory({
      _id: category._id,
      name: category.name,
    });
    setCategoryDialogOpen(true);
  };

  const columns = [
    { field: "id", headerName: "S. No", width: 70 },
    { field: "title", headerName: "Title", width: 200 },
    { field: "intro", headerName: "Intro", width: 300 },
    {
      field: "category",
      headerName: "Category",
      width: 150,
      valueGetter: (category) => (category ? category.name : "Uncategorized"),
    },
    {
      field: "coverImage",
      headerName: "Cover Image",
      width: 120,
      renderCell: (params) => (
        <Avatar src={params.value} variant="square" sx={{ width: 80, height: 50 }} />
      ),
    },
    { field: "status", headerName: "Status", width: 100 },
    {
      field: "publishDate",
      headerName: "Publish Date",
      width: 150,
      valueFormatter: (value) => (value ? dayjs(value).format("MMM DD, YYYY") : "Not Set"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row.id - 1)}>
            <EditIcon />
          </IconButton>
          <IconButton color="error" onClick={() => handleDelete(params.row.id - 1)}>
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const rows = blogs?.map((blog, index) => ({
    id: index + 1,
    ...blog,
  }));

  const paginationModel = { page: 0, pageSize: 10 };

  return (
    <Layout>
      <div style={{ padding: "1rem" }}>
        <Typography variant="h5" paddingY={1}>
          Manage Blogs
        </Typography>

        <Paper sx={{ padding: 1, maxWidth: "100%", display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="contained" startIcon={<CategoryIcon />} onClick={handleCategoryDialogOpen}>
            Manage Categories
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            Add Blog
          </Button>
        </Paper>

        <Paper sx={{ width: "100%", marginBottom: 3 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{ pagination: { paginationModel } }}
            pageSizeOptions={[5, 10]}
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
            }}
          />
        </Paper>

        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
          <DialogTitle>{editIndex !== null ? "Edit Blog" : "Add Blog"}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Title"
              variant="outlined"
              margin="dense"
              value={currentBlog.title}
              onChange={(e) => setCurrentBlog((prev) => ({ ...prev, title: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Slug (URL)"
              variant="outlined"
              margin="dense"
              value={currentBlog.slug || currentBlog.title.toLowerCase().replace(/\s+/g, "-")}
              onChange={(e) => setCurrentBlog((prev) => ({ ...prev, slug: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <Button component="label" variant="outlined" fullWidth sx={{ mb: 2 }}>
              <UploadFileIcon sx={{ marginRight: 1 }} /> Upload Cover Image
              <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, "coverImage")} />
            </Button>
            {currentBlog.coverImagePreview && (
              <Avatar src={currentBlog.coverImagePreview} variant="square" sx={{ width: 100, height: 60, mb: 2 }} />
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="category-label">Blog Category</InputLabel>
              <Select
                labelId="category-label"
                id="category-select"
                value={currentBlog.category || ""}
                label="Blog Category"
                onChange={handleCategoryChange}
              >
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextareaAutosize
              minRows={3}
              placeholder="Blog Intro (150-200 characters)"
              value={currentBlog.intro}
              onChange={(e) => setCurrentBlog((prev) => ({ ...prev, intro: e.target.value }))}
              style={{ width: "100%", marginBottom: "16px", padding: "8px", resize: "vertical" }}
            />

            <Typography variant="h6">Main Content Sections</Typography>
            {currentBlog.contentSections.map((section, index) => (
              <div key={index} style={{ border: "1px solid #ddd", padding: "16px", marginBottom: "16px" }}>
                <TextField
                  fullWidth
                  label="Heading"
                  variant="outlined"
                  margin="dense"
                  value={section.heading}
                  onChange={(e) => handleSectionChange(index, "heading", e.target.value)}
                  sx={{ mb: 2 }}
                />
                <ReactQuill
                  value={section.content}
                  onChange={(value) => handleSectionChange(index, "content", value)}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ marginBottom: "16px" }}
                />
                <Button component="label" variant="outlined" fullWidth sx={{ mb: 2 }}>
                  <UploadFileIcon sx={{ marginRight: 1 }} /> Upload Section Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "image", index)}
                  />
                </Button>
                {section.imagePreview && (
                  <Avatar src={section.imagePreview} variant="square" sx={{ width: 100, height: 60, mb: 2 }} />
                )}
              </div>
            ))}
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddSection}>
              Add Section
            </Button>

            <TextField
              fullWidth
              label="Meta Title (Max 60 characters)"
              variant="outlined"
              margin="dense"
              value={currentBlog.metaTitle}
              onChange={(e) => setCurrentBlog((prev) => ({ ...prev, metaTitle: e.target.value }))}
              sx={{ mb: 2, mt: 2 }}
            />

            <TextareaAutosize
              minRows={3}
              placeholder="Meta Description (Max 160 characters)"
              value={currentBlog.metaDescription}
              onChange={(e) => setCurrentBlog((prev) => ({ ...prev, metaDescription: e.target.value }))}
              style={{ width: "100%", marginBottom: "16px", padding: "8px", resize: "vertical" }}
            />

            <TextField
              fullWidth
              label="Meta Keywords (Comma-separated)"
              variant="outlined"
              margin="dense"
              value={currentBlog.metaKeywords.join(", ")}
              onChange={handleMetaKeywordsChange}
              sx={{ mb: 2 }}
            />
            <div style={{ marginBottom: "16px" }}>
              {currentBlog.metaKeywords.map((keyword, index) => (
                <Chip
                  key={index}
                  label={keyword}
                  onDelete={() =>
                    setCurrentBlog((prev) => ({
                      ...prev,
                      metaKeywords: prev.metaKeywords.filter((_, i) => i !== index),
                    }))
                  }
                  sx={{ marginRight: 1, marginBottom: 1 }}
                />
              ))}
            </div>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Publish Date"
                value={currentBlog.publishDate}
                onChange={(newValue) => setCurrentBlog((prev) => ({ ...prev, publishDate: newValue }))}
                renderInput={(params) => <TextField {...params} fullWidth sx={{ mb: 2 }} />}
              />
            </LocalizationProvider>

            <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                id="status-select"
                value={currentBlog.status}
                label="Status"
                onChange={(e) => setCurrentBlog((prev) => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Published">Published</MenuItem>
                <MenuItem value="Scheduled">Scheduled</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAddOrEdit}
              disabled={!currentBlog.title || !currentBlog.intro || !currentBlog.category || isSubmitting}
              endIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {editIndex !== null ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={categoryDialogOpen} onClose={handleCategoryDialogClose} fullWidth maxWidth="sm">
          <DialogTitle>Manage Categories</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Category Name"
              variant="outlined"
              margin="dense"
              value={currentCategory.name}
              onChange={(e) => setCurrentCategory((prev) => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <Paper sx={{ width: "100%", marginTop: 2 }}>
              {categories.map((category) => (
                <div
                  key={category._id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 16px",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <Typography>{category.name}</Typography>
                  <div>
                    <IconButton color="primary" onClick={() => handleEditCategory(category)}>
                      <EditIcon />
                    </IconButton>
                  </div>
                </div>
              ))}
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCategoryDialogClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCategorySubmit}
              disabled={!currentCategory.name.trim()}
            >
              {currentCategory._id ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Blogs;